<template>
  <div class="historial-simple-wrapper">
    <!-- Encabezado simple -->
    <div class="page-header mb-5">
      <div class="page-header-icon">
        <v-icon color="white" size="26">mdi-account-question-outline</v-icon>
      </div>
      <div>
        <h2 class="page-title">Historial de Consultas de Colillas</h2>
        <p class="page-subtitle">Últimas 15 consultas de colillas realizadas por el usuario</p>
      </div>
    </div>

    <!-- Tarjeta de Búsqueda Única y Directa -->
    <v-card class="elevation-2 rounded-lg pa-6 mb-5">
      <v-row align="center">
        <v-col cols="12" md="9">
          <v-text-field
            v-model.trim="busqueda"
            label="Buscar por Nombre o Cédula del usuario"
            placeholder="Ej. 1010112750 o Juan Diego..."
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-account-search-outline"
            color="#294697"
            hide-details
            @keyup.enter="buscar"
            @click:clear="limpiar"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-btn
            block
            color="#294697"
            dark
            height="40"
            :loading="cargando"
            @click="buscar"
          >
            <v-icon left>mdi-magnify</v-icon>
            BUSCAR
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Estado inicial sin búsqueda -->
    <v-alert
      v-if="!busquedaRealizada"
      type="info"
      text
      dense
      icon="mdi-information-outline"
      class="elevation-1 rounded-lg pa-4"
    >
      Ingresa el nombre o la cédula de un usuario para consultar sus últimas 15 colillas revisadas o impresas.
    </v-alert>

    <!-- Estado sin resultados -->
    <v-alert
      v-else-if="items.length === 0"
      type="warning"
      text
      dense
      icon="mdi-alert-circle-outline"
      class="elevation-1 rounded-lg pa-4"
    >
      No se encontraron consultas registradas para este usuario.
    </v-alert>

    <!-- Lista limpia y desaturada de resultados -->
    <v-card v-else class="elevation-2 rounded-lg pa-4">
      <div class="d-flex align-center justify-space-between mb-3 px-2">
        <span class="font-weight-bold grey--text text--darken-3">
          Se encontraron {{ items.length }} registro(s)
        </span>
      </div>

      <v-simple-table dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Fecha y Hora</th>
              <th class="text-left">Usuario</th>
              <th class="text-left">Acción</th>
              <th class="text-left">Colilla / Transacción</th>
              <th class="text-left">Valor</th>
              <th class="text-left">Resultado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>
                <div class="text-caption">
                  <v-icon x-small class="mr-1" color="#294697">mdi-clock-outline</v-icon>
                  {{ formatearFecha(item.fecha_hora) }}
                </div>
              </td>
              <td>
                <div class="font-weight-bold text-subtitle-2">{{ item.usuario_nombre }}</div>
                <div class="text-caption grey--text">C.C. {{ item.usuario_identificacion }}</div>
              </td>
              <td>
                <v-chip
                  x-small
                  :color="item.tipo_accion === 'IMPRESION' ? 'teal lighten-5' : 'indigo lighten-5'"
                  :text-color="item.tipo_accion === 'IMPRESION' ? 'teal darken-3' : 'indigo darken-3'"
                  class="font-weight-bold"
                >
                  <v-icon left x-small>{{ item.tipo_accion === 'IMPRESION' ? 'mdi-printer' : 'mdi-magnify' }}</v-icon>
                  {{ item.tipo_accion }}
                </v-chip>
              </td>
              <td>
                <span class="font-weight-bold">{{ item.colilla_referencia }}</span>
              </td>
              <td>
                <span v-if="item.valor_premio" class="green--text text--darken-2 font-weight-bold">
                  {{ formatCurrency(item.valor_premio) }}
                </span>
                <span v-else class="grey--text">-</span>
              </td>
              <td>
                <v-chip x-small :color="colorResultado(item.resultado).bg" :text-color="colorResultado(item.resultado).text">
                  {{ item.resultado }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import env from "../../env";
import Swal from "sweetalert2";

export default {
  name: "HistorialConsultas",
  data() {
    return {
      cargando: false,
      busqueda: "",
      busquedaRealizada: false,
      items: []
    };
  },
  methods: {
    async buscar() {
      if (!this.busqueda || !this.busqueda.trim()) {
        Swal.fire({
          icon: "info",
          title: "Atención",
          text: "Ingresa el nombre o la cédula del usuario para consultar.",
          confirmButtonColor: "#294697"
        });
        return;
      }

      this.cargando = true;
      this.busquedaRealizada = true;
      try {
        const idUsuarioAdmin = localStorage.getItem("id");
        const params = new URLSearchParams({
          id_usuario: idUsuarioAdmin != null ? String(idUsuarioAdmin) : "",
          usuario: this.busqueda.trim(),
          limit: "100"
        });

        const response = await axios.get(`${env.endpoint}premios/historial?${params.toString()}`);
        if (response.data && response.data.status) {
          this.items = response.data.items || [];
        } else {
          this.items = [];
        }
      } catch (error) {
        console.error("Error al consultar historial:", error);
        this.items = [];
      } finally {
        this.cargando = false;
      }
    },
    limpiar() {
      this.busqueda = "";
      this.items = [];
      this.busquedaRealizada = false;
    },
    colorResultado(resultado) {
      switch (resultado) {
        case "EXITOSA":
          return { bg: "green lighten-5", text: "green darken-3" };
        case "NO_ENCONTRADA":
          return { bg: "red lighten-5", text: "red darken-3" };
        case "OFICINA_PRINCIPAL":
          return { bg: "amber lighten-5", text: "amber darken-4" };
        case "PAGADO":
          return { bg: "blue lighten-5", text: "blue darken-3" };
        default:
          return { bg: "grey lighten-3", text: "grey darken-3" };
      }
    },
    formatearFecha(fechaStr) {
      if (!fechaStr) return "-";
      const fecha = new Date(fechaStr.replace(/-/g, "/"));
      if (isNaN(fecha.getTime())) return fechaStr;
      return fecha.toLocaleString("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0
      }).format(value);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap');

.historial-simple-wrapper {
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
}

.page-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #294697, #1D3C7F);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(41, 70, 151, 0.25);
}

.page-title {
  font-family: 'Poppins', sans-serif;
  font-size: 1.35rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.page-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}
</style>
